import { PrivilegeEnum } from '@/api/ApiClient';
import type { Operation } from '@/stores/authStore';

type IsAuthorizedFn = (privilege: PrivilegeEnum | null | undefined, op: Operation) => boolean;

/**
 * Note privilege checks. Note is broader than OwnNote.
 * - Create: OwnNote only (always creating your own)
 * - Read/Update/Delete: Note (all) OR OwnNote (own only)
 */
export function canCreateNote(isAuthorized: IsAuthorizedFn): boolean {
  return isAuthorized(PrivilegeEnum.OwnNote, 'create');
}

export function canReadNote(
  isAuthorized: IsAuthorizedFn,
  note: { lastChangedByUser?: string } | null,
  currentUserId: string | undefined
): boolean {
  const isOwn = !!note && note.lastChangedByUser === currentUserId;
  return isAuthorized(PrivilegeEnum.Note, 'read') || (isOwn && isAuthorized(PrivilegeEnum.OwnNote, 'read'));
}

export function canUpdateNote(
  isAuthorized: IsAuthorizedFn,
  note: { lastChangedByUser?: string } | null,
  currentUserId: string | undefined
): boolean {
  const isOwn = !!note && note.lastChangedByUser === currentUserId;
  return isAuthorized(PrivilegeEnum.Note, 'update') || (isOwn && isAuthorized(PrivilegeEnum.OwnNote, 'update'));
}

export function canDeleteNote(
  isAuthorized: IsAuthorizedFn,
  note: { lastChangedByUser?: string } | null,
  currentUserId: string | undefined
): boolean {
  const isOwn = !!note && note.lastChangedByUser === currentUserId;
  return isAuthorized(PrivilegeEnum.Note, 'delete') || (isOwn && isAuthorized(PrivilegeEnum.OwnNote, 'delete'));
}

/**
 * File privilege checks. File is broader than OwnFile.
 * - Create: OwnFile only (always creating your own)
 * - Read/Update/Delete: File (all) OR OwnFile (own only)
 */
export function canCreateFile(isAuthorized: IsAuthorizedFn): boolean {
  return isAuthorized(PrivilegeEnum.OwnFile, 'create');
}

export function canReadFile(
  isAuthorized: IsAuthorizedFn,
  file: { lastChangedByUser?: string } | null,
  currentUserId: string | undefined
): boolean {
  const isOwn = !!file && file.lastChangedByUser === currentUserId;
  return isAuthorized(PrivilegeEnum.File, 'read') || (isOwn && isAuthorized(PrivilegeEnum.OwnFile, 'read'));
}

export function canUpdateFile(
  isAuthorized: IsAuthorizedFn,
  file: { lastChangedByUser?: string } | null,
  currentUserId: string | undefined
): boolean {
  const isOwn = !!file && file.lastChangedByUser === currentUserId;
  return isAuthorized(PrivilegeEnum.File, 'update') || (isOwn && isAuthorized(PrivilegeEnum.OwnFile, 'update'));
}

export function canDeleteFile(
  isAuthorized: IsAuthorizedFn,
  file: { lastChangedByUser?: string } | null,
  currentUserId: string | undefined
): boolean {
  const isOwn = !!file && file.lastChangedByUser === currentUserId;
  return isAuthorized(PrivilegeEnum.File, 'delete') || (isOwn && isAuthorized(PrivilegeEnum.OwnFile, 'delete'));
}
